import colors from 'tailwindcss/colors';
import moment from 'moment';
import MarketRegionState from '@app/model/market_area/MarketAreaState';
import { marketRegionStateClusterColors, marketRegionStateClusterNames } from '@app/model/market_area/MarketRegionStateCluster';
import {
	MarketPaceDay, MarketPaceLeadInterval, MarketPaceReason, MarketPaceRun, MarketPaceSource, MarketPaceStep, MarketPaceVerdict,
} from '@app/model/market_insight/MarketPace';
import { PACE_CELL_COLOR, PACE_CLASS_OF } from './MarketPaceStyles';

// The words of the pace strip (grill of 2026-09-29, decisions 2, 14, 16, 19 and P5).
// The screen shows words only: no room count, no level, no percent.

export const PACE_VERDICT_LABEL: Record<MarketPaceVerdict, string> = {
	MUCH_FASTER: 'Much faster than the market',
	FASTER: 'Faster than the market',
	IN_LINE: 'In line with the market',
	SLOWER: 'Slower than the market',
	MUCH_SLOWER: 'Much slower than the market',
	NO_COMPARISON: 'No comparison',
};

export const PACE_VERDICT_SHORT: Record<MarketPaceVerdict, string> = {
	MUCH_FASTER: 'Much faster',
	FASTER: 'Faster',
	IN_LINE: 'In line',
	SLOWER: 'Slower',
	MUCH_SLOWER: 'Much slower',
	NO_COMPARISON: 'No comparison',
};

export const PACE_LEGEND_ORDER: MarketPaceVerdict[] = ['MUCH_FASTER', 'FASTER', 'IN_LINE', 'SLOWER', 'MUCH_SLOWER', 'NO_COMPARISON'];

// Why a stay date has no comparison. SMALL_MOVE has no line here: its explanation sentence says it.
const REASON_TEXT: Record<Exclude<MarketPaceReason, 'SMALL_MOVE'>, string> = {
	MARKET_TOO_SMALL: 'Too few market hotels with enough history.',
	NO_OWN_HISTORY: 'Your hotel has no history this far ahead.',
	NO_DATA: 'No Booking.com data for this night.',
	NO_RECENT_RUN: 'No pace result in the last 3 days.',
};

export const reasonText = (day: MarketPaceDay): string | null =>
	day.verdict === 'NO_COMPARISON' && day.reason !== null && day.reason !== 'SMALL_MOVE' ? REASON_TEXT[day.reason] : null;

// The "usual" of each side: the history of the same Lead Interval.
const LEAD_PHRASE: Record<MarketPaceLeadInterval, string> = {
	DAYS_0_15: 'this close to arrival',
	DAYS_16_60: 'a few weeks out',
	DAYS_61_PLUS: 'this far ahead',
};

const OWN_PHRASE: Record<MarketPaceStep, string> = {
	FASTER: 'are moving faster than usual',
	LITTLE_FASTER: 'are moving a little faster than usual',
	USUAL: 'are moving at your usual pace',
	LITTLE_SLOWER: 'are moving a little slower than usual',
	SLOWER: 'are moving slower than usual',
	CAME_BACK: 'came back on sale',
};

const MARKET_SENTENCE: Record<MarketPaceStep, string> = {
	FASTER: 'Across the market, rooms are moving faster than usual.',
	LITTLE_FASTER: 'Across the market, rooms are moving faster than usual.',
	USUAL: 'Across the market, rooms are moving at their usual pace.',
	LITTLE_SLOWER: 'Across the market, rooms are moving slower than usual.',
	SLOWER: 'Across the market, rooms are moving slower than usual.',
	CAME_BACK: 'Across the market, rooms are moving slower than usual.',
};

// Why this night got its verdict, for that night (weekday) and its lead time. Each side is told
// against its own usual pace, so the sentences agree with the verdict. An in-line verdict gets one
// sentence: a breakdown of a gap under 2 levels would read as a contradiction.
export const explanationLines = (day: MarketPaceDay): string[] => {
	if (day.verdict === 'NO_COMPARISON') return [];
	const night = `this ${moment(day.statDate).format('dddd')}`;
	const when = LEAD_PHRASE[day.leadInterval];
	if (day.reason === 'SMALL_MOVE') return [`Your rooms for ${night} barely moved, too little to compare.`];
	if (day.verdict === 'IN_LINE') return [`Your rooms for ${night} are moving in step with the market ${when}.`];
	const lines: string[] = [];
	if (day.ownStep === 'CAME_BACK') lines.push(`Some of your rooms for ${night} came back on sale.`);
	else if (day.ownStep !== null) lines.push(`Your rooms for ${night} ${OWN_PHRASE[day.ownStep]} ${when}.`);
	if (day.marketStep !== null) lines.push(MARKET_SENTENCE[day.marketStep]);
	return lines;
};

// The method, in the (i) next to "Your pace vs market". No data numbers (decision 16). The first
// line names the data source of the hotel (decision P5).
export const methodLines = (source: MarketPaceSource): string[] => [
	source === 'PMS'
		? 'For your hotel, we count the rooms booked in your PMS, on all channels. For the market, we count the rooms that left Booking.com.'
		: 'We count the rooms that left Booking.com in the last 3 days, for your hotel and for each hotel of your market.',
	'Each hotel is compared with its own usual pace: its moves over the last 90 days, for dates about as far ahead. So a small hotel and a big hotel count the same.',
	'The market is the hotels of your market area, without your hotel. A hotel counts only when it has enough history, and we compare only when enough hotels count.',
	'Faster or slower: your hotel moved clearly more or less than the market, each against its own usual pace. Much faster or much slower: the difference is very large.',
	'Slower usually means that some of your rooms came back on sale. A move of 1 room never counts.',
	'An indication of demand, not confirmed reservations.',
];

// The label of the run in the header of the panel (decision 10): "Pace: 22 → 25 Sep" (one month) or
// "Pace: 30 Sep → 2 Oct" (two months); an older run "Pace from 25 Sep"; no run "No pace comparison".
// The labels are short because the header has one line at 400 px, next to "Market Demand Calendar".
export const runLabel = (run: MarketPaceRun | null): string => {
	if (run === null) return 'No pace comparison';
	const start = moment(run.startDay);
	const end = moment(run.endDay);
	if (!run.isLatest) return `Pace from ${end.format('D MMM')}`;
	return `Pace: ${start.isSame(end, 'month') ? start.format('D') : start.format('D MMM')} → ${end.format('D MMM')}`;
};

export const PACE_ERROR_LABEL = 'Pace is not available';

// ---- the hovers (HTML for the Highcharts tooltip and the MonthGrid tooltip) ----------------------
const escape = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const box = (inner: string) => `<div style="width:260px;font-size:12px;line-height:16px;color:${colors.gray[600]};white-space:normal">${inner}</div>`;
const head = (statDate: string) => `<div style="font-weight:600;color:${colors.gray[900]}">${moment(statDate).format('ddd D MMMM YYYY')}</div>`;

const demandLine = (state: MarketRegionState) =>
	`<div style="margin-top:6px"><span style="display:inline-block;width:8px;height:8px;border-radius:9999px;margin-right:6px;background:${marketRegionStateClusterColors[state.cluster]}"></span>Demand: ${marketRegionStateClusterNames[state.cluster]}</div>`;

const verdictLine = (day: MarketPaceDay, marginTop: number) => {
	const cls = PACE_CLASS_OF[day.verdict];
	const ring = cls === 'NONE' ? `box-shadow:inset 0 0 0 1px ${colors.gray[400]}` : '';
	return `<div style="margin-top:${marginTop}px;font-weight:500;color:${colors.gray[900]}"><span style="display:inline-block;width:8px;height:8px;border-radius:2px;margin-right:6px;background:${PACE_CELL_COLOR[cls]};${ring}"></span>${PACE_VERDICT_LABEL[day.verdict]}</div>`;
};

const explainHtml = (day: MarketPaceDay) => {
	const why = reasonText(day);
	if (why !== null) return `<div style="margin-top:4px;color:${colors.gray[500]}">${escape(why)}</div>`;
	return `<div style="margin-top:4px">${explanationLines(day).map(line => `<div style="margin-top:2px">${escape(line)}</div>`).join('')}</div>`;
};

// A demand bar: the date and the demand level only.
export const demandHoverHtml = (state: MarketRegionState) => box(head(state.statDate) + demandLine(state));

// A pace cell under the chart: the date, the verdict and why, for that night.
export const paceHoverHtml = (day: MarketPaceDay) => box(head(day.statDate) + verdictLine(day, 6) + explainHtml(day));

// A calendar day: the demand and the pace together. Without a pace result, the demand only.
export const dayHoverHtml = (state: MarketRegionState, day: MarketPaceDay | undefined) =>
	box(head(state.statDate) + demandLine(state) + (day !== undefined ? verdictLine(day, 2) + explainHtml(day) : ''));
